import React, { useMemo, useState } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { formatAmount, formatMiles, fromStorage } from '../../../utils';
import { FallbackImage } from '../../../components';
import { Photo } from '../../../assets/img';
import { MaintenanceService } from '../../../services';

const ModalViewReport = ({ item, onClose }) => {
  const [loading, setLoading] = useState(false);

  const suppliers = useMemo(() => {
    const providerIds = [];

    item?.supplies?.forEach(s => {
      if (providerIds.includes(s.supplier_id)) return;
      providerIds.push(s.supplier_id);
    });

    return providerIds?.map(id => {
      const name = item?.supplies?.find(s => s.supplier_id === id)?.supplier?.name;
      const amount = item?.supplies
        ?.filter(y => y.supplier_id === id)
        ?.reduce((acc, curr) => acc + +curr.total, 0);

      return {
        name,
        amount,
        amount_local: amount * item?.cost_conversion,
      }
    });
  }, [item]);

  const onPrint = async () => {
    setLoading(true);
    try {
      const response = await MaintenanceService.moderator.downloadOne(item?.id);
      if (response.url) {
        const link = document.createElement('a');
        link.href = fromStorage(response.url);
        link.target = '_blank';
        link.download = response.url.split('/').pop();
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      toast.error(String(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-[36rem] mx-auto bg-white p-6 rounded-2xl font-sans antialiased selection:bg-orange-100 relative">
      <button
        type="button"
        className="p-1.5 rounded-full hover:bg-gray5 transition-colors absolute top-2 right-2"
        onClick={onClose}
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-text">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="mb-6">
        <span className="text-sm font-semibold text-gray-500 block mb-0.5">Propietario</span>
        <h2 className="text-xl font-bold mb-5 text-gray-900">{ item?.owner?.person?.fullName || 'Maxer' }</h2>

        <div className="grid grid-cols-2 gap-4 pb-3 border-b border-orange">
          <div>
            <span className="text-sm text-default block">Placa</span>
            <span className="text-lg font-bold text-gray-800">{ item?.vehicle?.plaque }</span>
          </div>
          <div>
            <span className="text-sm text-default block">Fecha al finalizar del mantenimiento</span>
            <span className="text-lg font-medium text-gray-700">{ moment(item?.finished_at).format('DD-MM-YY') }</span>
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-6">
        <div>
          <span className="text-default font-medium mr-2">Categoría de mantenimiento:</span>
          <span className="text-gray-900 font-medium text-lg">{ item?.category?.name }</span>
        </div>
        <div>
          <span className="text-default font-normal mr-2">Subcategoría de mantenimiento:</span>
          <span className="text-gray-900 font-medium text-lg">{ item?.subcategory?.name }</span>
        </div>
        <div>
          <span className="text-default font-normal mr-2">Kilometraje:</span>
          <span className="text-gray-900 font-medium text-lg">{ formatMiles(item?.mileage, false, '').trim() }</span>
        </div>
        {/* <div>
          <span className="text-default font-normal mr-2">Acción inmediata:</span>
          <span className="text-gray-900 font-medium text-lg">{ item?.action }</span>
        </div> */}
      </div>

      <div className="mb-6">
        <h3 className="text-xl font-bold mb-4 text-gray-900">Datos de repuestos e insumos</h3>
        <div className="space-y-3">
          {item?.supplies?.map((s, index) => (
            <div key={index} className="bg-[#F4F7FC] rounded-xl p-4 grid grid-cols-4 gap-2 text-sm shadow-sm border border-gray-100">
              <div>
                <span className="text-default font-semibold block mb-1">Nombre</span>
                <span className="font-medium text-gray-800">{ s.supply?.name }</span>
              </div>
              <div>
                <span className="text-default font-semibold block mb-1">Cantidad</span>
                <span className="font-medium text-gray-800">{ s.quantity }</span>
              </div>
              <div>
                <span className="text-default font-semibold block mb-1">Uni. Medida</span>
                <span className="font-medium text-gray-800">{ s.measuring_unit?.name }</span>
              </div>
              <div>
                <span className="text-default font-semibold block mb-1">Costo ($)</span>
                <span className="font-medium text-gray-800">{ formatAmount(s.total) }</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
        <div className="sm:col-span-1">
          <h4 className="text-xl font-bold mb-3 text-gray-900">Evidencia</h4>
          <div className="w-28 h-28 rounded-xl overflow-hidden border border-gray-200 shadow-inner">
            <FallbackImage
              src={item?.evidence}
              placeholder={Photo}
              className="w-full h-full object-cover"
              placeholderClassName="w-full h-full object-cover"
              alt="Evidencia del mantenimiento"
            />
          </div>
        </div>
        <div className="sm:col-span-2">
          <h4 className="text-xl font-bold mb-3 text-gray-900">Observaciones</h4>
          <div className="w-full min-h-[112px] p-3 border border-gray-950 rounded-md text-gray-700 bg-white leading-relaxed text-sm">
            { item?.observations }
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h4 className="text-xl font-bold mb-4 text-gray-900">Gastos por repuestos e Insumos</h4>

        <div className="bg-orange text-white p-4 rounded-xl flex justify-between items-center shadow-md">
          <div className="flex flex-col">
            <span className="text-base font-semibold">Total por</span>
            <span className="text-base font-semibold">Categoría</span>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold">${ formatAmount(item?.cost_total) }</div>
            <div className="font-medium opacity-90">Bs { formatAmount(item?.cost_total_local) }</div>
          </div>
        </div>

        <div className="bg-[#000] text-white p-4 rounded-xl space-y-4 shadow-md mt-2">
          <span className="font-semibold text-white block">Total por Proveedor</span>

          {suppliers.map((prov, index) => (
            <div key={index} className="border-b border-gray-800 last:border-none pb-3 last:pb-0">
              <div className="flex justify-between items-center">
                <span className="text-base font-semibold text-gray-100">{ prov.name }</span>
                <div className="text-right">
                  <span className="text-base font-bold block">{ formatAmount(prov.amount, '$') }</span>
                  <span className="text-gray-400">{ formatAmount(prov.amount_local, 'Bs') }</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center items-center gap-6 pt-2">
        <button
          onClick={onPrint}
          disabled={loading}
          className="text-[#4D7CFF] flex items-center gap-2 font-bold text-lg hover:underline transition-all duration-200"
        >
          {loading && (
            <svg className="animate-spin w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {loading ? 'Descargando...' : 'Imprimir'}
        </button>
        <button
          onClick={onClose}
          className="text-[#A3A3A3] font-bold text-lg hover:text-gray-600 transition-all duration-200"
        >
          Cerrar
        </button>
      </div>

    </div>
  )
}

export default ModalViewReport